import { useCallback, useMemo } from "react";
import { ensureExhaustive } from "@b2bportal/core-utilities";
import type { RoomProduct } from "@b2bportal/lodging-api";
import { CancellationPolicyV2Enum, MealPlanKind } from "@b2bportal/lodging-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { useMealPlanHelpers } from "@tenant/hotels";
import type { HotelRoomRateProps } from "./HotelRoomRate";

export type UseHotelRoomRateReturn = {
  context: { product: RoomProduct };
  text: {
    title: string | undefined;
    subtitle: string | undefined;
  };
  mealPlans?: {
    planName: string | undefined;
    subtitle: string | undefined;
    finePrint: string | undefined;
  };
};

export const useHotelRoomRate = ({
  product,
}: HotelRoomRateProps): UseHotelRoomRateReturn => {
  const { t } = useI18nContext();

  const {
    enableMealPlans,
    handlers: { getMealPlanNameFromRoomProduct },
  } = useMealPlanHelpers();

  const rateText = useMemo(() => {
    if (product.cancellationPolicyV2 == null) {
      return {
        title: "", //TODO
      };
    }

    switch (product.cancellationPolicyV2.CancellationPolicyV2) {
      case CancellationPolicyV2Enum.FullyRefundable: {
        return {
          title: t("core-hotels.roomSelection.refundable"),
          subtitle: product.cancellationPolicyV2?.primaryText,
        };
      }
      case CancellationPolicyV2Enum.NonRefundable: {
        return {
          title: product.cancellationPolicyV2?.primaryText,
        };
      }
      case CancellationPolicyV2Enum.PartiallyRefundable: {
        return {
          title: product.cancellationPolicyV2?.primaryText,
        };
      }
      case CancellationPolicyV2Enum.Unknown: {
        return {
          title: product.cancellationPolicyV2?.primaryText,
        };
      }
      default: {
        return ensureExhaustive(product.cancellationPolicyV2, {
          title: "",
        });
      }
    }
  }, [product.cancellationPolicyV2, t]);

  const getMealPlanNameForRoomRateLabel = useCallback(
    (product: RoomProduct) => {
      if (product.mealPlan?.kind === MealPlanKind.RoomOnly) {
        return t("core-hotels.mealPlan.noThanks");
      }

      return getMealPlanNameFromRoomProduct(product);
    },
    [getMealPlanNameFromRoomProduct, t]
  );

  return useMemo(
    (): UseHotelRoomRateReturn => ({
      context: { product },
      text: {
        title: rateText.title,
        subtitle: rateText.subtitle,
      },
      mealPlans: enableMealPlans
        ? {
            planName: getMealPlanNameForRoomRateLabel(product),
            subtitle: rateText.title,
            finePrint: rateText.subtitle,
          }
        : undefined,
    }),
    [
      enableMealPlans,
      getMealPlanNameForRoomRateLabel,
      product,
      rateText.subtitle,
      rateText.title,
    ]
  );
};
